import { viewService } from '@unpispas/upp-base';

import { ViewType, ViewObject } from '../base';

import { dataService } from '../../data';
import { ticketService, TicketInfo } from '../../tickets';
import { Audit } from '../objects/audit';

import type { PlaceView } from './place';
import type { TillView } from './till';

import type { AuditInfoReport } from '../objects/audit';

/****************************/
/* CLASS VIEW               */
/****************************/

export class _AuditView extends ViewObject<Audit> {

    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get audit(): Audit {
        return this.object;
    }

    constructor(object: Audit, data: dataService) {
        super(object, data);
    }

    private get tickets(): ticketService {
        return this.data.injector.get(ticketService);
    }

    private get view(): viewService {
        return this.data.injector.get(viewService);
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION       */
    /**********************************/

    private _validReport: AuditInfoReport | null = null;
    private _lastReport: AuditInfoReport = {
        schemaVersion: 0,
        payments: { cash: 0, card: 0, line: 0 },
        tickets: { completed: 0, cancelled: 0 },
        items: {
            completed: { amount: 0, income: 0 },
            cancelled: { amount: 0, income: 0 },
            onthehouse: { amount: 0, income: 0 },
        },
        activity: {
            addlines: { amount: 0, income: 0 },
            dellines: { amount: 0, income: 0 },
        },
        fiscal: {
            ticketbai: { ok: 0, ko: 0 },
            verifactu: { ok: 0, ko: 0 },
            ticketsii: { ok: 0, ko: 0 },
        },
        taxBreakdown: [],
    };

    private _ticketRows: TicketInfo[] = [];

    private _InvalidateReport(): void {
        if (this._validReport !== null){
            this._lastReport = this._validReport;
        }

        this._validReport = null;
        this._ticketRows = [];
    }

    private RefreshView(targets: string[]): void {
        if (targets.includes('TICKET')){
            this._InvalidateReport();
            this.DoRefreshView();
        }

        if (targets.includes('AUDIT')){
            this._InvalidateReport();
            this.DoRefreshView();
        }
    }
    
    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void {
        const weakref = new WeakRef(this);

        for (const _subscription of this._subscriptions) {
            _subscription.unsubscribe();
        }
        this._subscriptions = [];

        this._subscriptions.push(
            this.audit.OnRefresh.subscribe(
                (targets: string[]) => {
                    const self = weakref.deref();
                    if (self) {
                        self.RefreshView(targets);
                    }
                },
            ),
        );

        this._subscriptions.push(
            this.audit.OnChildChanged.subscribe(() => {
                const self = weakref.deref();
                if (self) {
                    self.DoRefreshView();
                }
            }),
        );

        const _place = this.audit.place;
        if (_place){
            this._subscriptions.push(
                _place.OnRefresh.subscribe(
                    (targets: string[]) => {
                        const self = weakref.deref();
                        if (self) {
                            self.RefreshView(targets);
                        }
                    },
                ),
            );
        }
    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/

    get place(): PlaceView | null {
        return this.alives.get(this.audit.place) as PlaceView | null;
    }

    get till(): TillView | null {
        return this.alives.get(this.audit.till) as TillView | null;
    }

    /**********************************/
    /* PUBLIC API                     */
    /**********************************/

    private _isloading = false;
    get isLoading(): boolean {
        return this._isloading;
    }

    set isLoading(value: boolean) {
        if (this._isloading === value){
            return;
        }

        this._isloading = value;
        this.DoRefreshView();
    }

    get Report(): AuditInfoReport | null {
        if (this.audit.IsPending){
            if (this._validReport !== null){
                return this._validReport;
            }

            if (this.view.IsOnline){
                this.isLoading = true;
                this.LoadReport().then((_report) => {
                    this._lastReport = _report;
                    this._validReport = _report;                    
                    this.isLoading = false;
                });
            }

            return this._lastReport;
        }
        else {
            return this.audit.Report;
        }
    }

    /**********************************/
    /* REPORT BUILD                   */
    /**********************************/

    /**
     * Loads till-scoped ticket rows for this audit.
     * Open (`PR`) periods use {@link Audit.ini}–{@link Audit.end} window rules ({@link _InAudit}).
     * Committed (`AC`) closes resolve rows by {@link TicketInfo} `intill` (= `TICKET.audit` at commit).
     * Does not mutate the audit; {@link LoadReport} assigns period bounds for open (`PR`) audits.
     * Features must not call {@link ticketService} directly.
     */
    async LoadTickets(): Promise<TicketInfo[]> {
        if (!this.place || !this.till || !this.view.IsOnline){
            return [];
        }

        const _ini = this.audit.ini ?? null;
        const _end = this.audit.end ?? null;
        if (_ini == null || _end == null){
            return [];
        }

        if (this._ticketRows.length > 0){
            return this._ticketRows;
        }

        const _committed = !this.audit.IsPending && this.audit.objid != null;
        const _rows = await this.tickets.Query(
            this.place,
            _ini,
            _end,
            _committed ? 'CREATED' : 'UPDATED',
        );

        const _tcks = _committed
            ? this._ByAudit(_rows, String(this.audit.objid))
            : this._ByPeriod(_rows, _ini, _end);

        this._ticketRows = _tcks;

        return _tcks;
    }

    /** Committed close: rows assigned to this audit at commit (`place/ticketlst` field `intill`). */
    private _ByAudit(rows: TicketInfo[], audit: string): TicketInfo[] {
        const _tid = this.till?.objid ?? '';

        return rows.filter((row) => {
            if (row.tid == null || (row.tid ?? '') !== _tid){
                return false;
            }

            if (row.payment === 'PAYACCOUNT'){
                return false;
            }

            const _linked = String(row['intill'] ?? '').trim();
            return (_linked) !== '' && (_linked === audit);
        });
    }

    /** Open period: till-scoped rows whose paidon/updated fall in (ini, end]. */
    private _ByPeriod(rows: TicketInfo[], ini: Date, end: Date): TicketInfo[] {
        const _tid = this.till?.objid ?? '';

        return rows.filter((row) => {
            if (row.tid == null || (row.tid ?? '') !== _tid){
                return false;
            }

            if (row.payment === 'PAYACCOUNT'){
                return false;
            }

            return this._InAudit(row, ini, end);
        });
    }

    /**
     * Completed tickets count when paidon falls in (ini, end]; cancelled tickets when updated falls in that window.
     * Period bounds match server audit aggregation: start exclusive, end inclusive.
     */
    private _InAudit(row: TicketInfo, ini: Date, end: Date): boolean {
        const _iniMs = ini.getTime();
        const _endMs = end.getTime();
        const _cancelled = row.status === 'CC' || row.status === 'DE';

        if (_cancelled){
            if (row.updated == null){
                return false;
            }

            const _updatedMs = new Date(row.updated).getTime();
            return _updatedMs > _iniMs && _updatedMs <= _endMs;
        }

        const _paidon = row['paidon'];
        if (_paidon == null || _paidon === ''){
            return false;
        }

        const _paidonMs = new Date(String(_paidon)).getTime();
        return _paidonMs > _iniMs && _paidonMs <= _endMs;
    }

    /** Open-period (`PR`) aggregation only; caller must guard {@link Audit.IsPending} and online; caller caches the returned report when needed. */
    async LoadReport(): Promise<AuditInfoReport> {
        if (!this.place || !this.till){
            return this._lastReport;
        }

        if (this.audit.IsPending){
            const _previous = this.till.audits[0] ?? null;
            if (this.audit.ini == null){
                this.audit.ini = _previous?.end ?? new Date(0);
            }

            this.audit.end = new Date();
        }

        this._ticketRows = [];

        const _tcks = await this.LoadTickets();
        const _report: AuditInfoReport = {
            schemaVersion: 0,
            payments: this._AggregatePayments(_tcks),
            tickets: this._CountTickets(_tcks),
            items: this._AggregateItems(_tcks),
            activity: this._AggregateActivity(_tcks),
            fiscal: this._lastReport.fiscal,
            taxBreakdown: this._AggregateTaxBreakdown(_tcks),
        };

        return _report;
    }

    /**********************************/
    /* AGGREGATION                    */
    /**********************************/

    private _AggregatePayments(rows: TicketInfo[]): AuditInfoReport['payments'] {
        let _cash = 0;
        let _card = 0;
        let _line = 0;

        for (const _row of rows){
            const _payment = String(_row.payment ?? '').toUpperCase();
            const _price = this._ToNumber(_row.price);

            switch (_payment){
                case 'PAYCASH':
                    _cash += _price;
                    break;
                case 'PAYCARD':
                    _card += _price;
                    break;
                case 'PAYMIXED': {
                    const _mixed = this._ResolveMixedAmounts(_row);
                    _cash += _mixed.cash;
                    _card += _mixed.card;
                    break;
                }
                case 'PAYLINE':
                    _line += _price;
                    break;
                default:
                    break;
            }
        }

        return {
            cash: this._Round2(_cash),
            card: this._Round2(_card),
            line: this._Round2(_line),
        };
    }

    private _ResolveMixedAmounts(row: TicketInfo): { cash: number; card: number } {
        const _total = this._ToNumber(row.price);
        const _cashRaw = row['paycash'] ?? row['cash'] ?? row['mixedcash'] ?? null;
        const _cardRaw = row['paycard'] ?? row['card'] ?? row['mixedcard'] ?? null;
        const _cash = this._ToNumber(_cashRaw);
        const _card = this._ToNumber(_cardRaw);

        if (_cash <= 0 && _card <= 0){
            return { cash: 0, card: 0 };
        }

        if (this._Round2(_cash + _card) === this._Round2(_total)){
            return { cash: _cash, card: _card };
        }

        if ((_cash + _card) > _total && _total > 0){
            const _ratio = _total / (_cash + _card);
            return {
                cash: this._Round2(_cash * _ratio),
                card: this._Round2(_card * _ratio),
            };
        }

        const _normalizedCash = Math.max(0, _cash);
        return {
            cash: _normalizedCash,
            card: this._Round2(Math.max(0, _total - _normalizedCash)),
        };
    }

    private _CountTickets(rows: TicketInfo[]): AuditInfoReport['tickets'] {
        let _completed = 0;
        let _cancelled = 0;

        for (const _row of rows){
            const _status = String(_row.status ?? '').toUpperCase();
            if (_status === 'CC' || _status === 'DE'){
                _cancelled += 1;
            }
            else {
                _completed += 1;
            }
        }

        return { completed: _completed, cancelled: _cancelled };
    }

    private _AggregateActivity(rows: TicketInfo[]): AuditInfoReport['activity'] {
        let _addAmount = 0;
        let _addIncome = 0;
        let _delAmount = 0;
        let _delIncome = 0;

        for (const _row of rows){
            _addAmount += _row['addlines_amount'] as number;
            _addIncome += _row['addlines_income'] as number;
            _delAmount += _row['dellines_amount'] as number;
            _delIncome += _row['dellines_income'] as number;
        }

        return {
            addlines: {
                amount: _addAmount,
                income: _addIncome,
            },
            dellines: {
                amount: _delAmount,
                income: _delIncome,
            },
        };
    }

    private _AggregateItems(rows: TicketInfo[]): AuditInfoReport['items'] {
        let _completedAmount = 0;
        let _completedIncome = 0;
        let _cancelledAmount = 0;
        let _cancelledIncome = 0;
        let _onTheHouseAmount = 0;
        let _onTheHouseIncome = 0;

        for (const _row of rows){
            _completedAmount += this._ToNumber(_row['items_completed_amount']);
            _completedIncome += this._ToNumber(_row['items_completed_income']);
            _cancelledAmount += this._ToNumber(_row['items_cancelled_amount']);
            _cancelledIncome += this._ToNumber(_row['items_cancelled_income']);
            _onTheHouseAmount += this._ToNumber(_row['items_onthehouse_amount']);
            _onTheHouseIncome += this._ToNumber(_row['items_onthehouse_income']);
        }

        return {
            completed: {
                amount: _completedAmount,
                income: this._Round2(_completedIncome),
            },
            cancelled: {
                amount: _cancelledAmount,
                income: this._Round2(_cancelledIncome),
            },
            onthehouse: {
                amount: _onTheHouseAmount,
                income: this._Round2(_onTheHouseIncome),
            },
        };
    }

    private _AggregateTaxBreakdown(rows: TicketInfo[]): AuditInfoReport['taxBreakdown'] {
        type TaxAccumulator = {
            rate: number;
            base: number;
            taxes: number;
            total: number;
        };

        const _byRate = new Map<string, TaxAccumulator>();

        for (const _row of rows){
            const _status = String(_row.status ?? '').toUpperCase();
            if (_status === 'CC' || _status === 'DE'){
                continue;
            }

            for (const _tax of this._ParseTaxdetail(_row['taxdetail'])){
                const _key = _tax.rate.toFixed(2);
                const _current = _byRate.get(_key);
                const _base = Math.round(_tax.base * 100);
                const _taxes = Math.round(_tax.taxes * 100);
                const _total = Math.round(_tax.total * 100);

                if (_current){
                    _current.base += _base;
                    _current.taxes += _taxes;
                    _current.total += _total;
                }
                else {
                    _byRate.set(_key, {
                        rate: _tax.rate,
                        base: _base,
                        taxes: _taxes,
                        total: _total,
                    });
                }
            }
        }

        const _rows = Array.from(_byRate.values()).map((row) => {
            return {
                rate: row.rate,
                base: this._Round2(row.base / 100),
                taxes: this._Round2(row.taxes / 100),
                total: this._Round2(row.total / 100),
            };
        });

        _rows.sort((left, right) => right.rate - left.rate);
        return _rows;
    }

    private _ParseTaxdetail(raw: unknown): Array<{ rate: number; base: number; taxes: number; total: number }> {
        if (raw == null){
            return [];
        }

        let _entries: unknown = raw;
        if (typeof raw === 'string'){
            const _trimmed = raw.trim();
            if (_trimmed === ''){
                return [];
            }

            try {
                _entries = JSON.parse(_trimmed);
            }
            catch {
                return [];
            }
        }

        if (!Array.isArray(_entries)){
            return [];
        }

        const _rows: Array<{ rate: number; base: number; taxes: number; total: number }> = [];
        for (const _entry of _entries){
            if (_entry == null || typeof _entry !== 'object'){
                continue;
            }

            const _record = _entry as Record<string, unknown>;
            const _rate = this._ToNumber(_record['rate']);
            const _taxes = this._ToNumber(_record['amount'] ?? _record['taxes']);
            const _base = this._ToNumber(_record['base']);
            // Skip ghost rows only; keep economic 0% (base > 0, taxes = 0).
            if (_base === 0 && _taxes === 0){
                continue;
            }

            const _totalRaw = _record['total'];
            const _total = _totalRaw != null ? this._ToNumber(_totalRaw) : this._Round2(_base + _taxes);

            _rows.push({
                rate: this._Round2(_rate),
                base: this._Round2(_base),
                taxes: this._Round2(_taxes),
                total: this._Round2(_total),
            });
        }

        return _rows;
    }
    /**********************************/
    /* VALUE HELPERS                  */
    /**********************************/

    private _Round2(value: number): number {
        return Math.round(value * 100) / 100;
    }

    private _ToNumber(value: unknown): number {
        const _numeric = Number(value);
        return Number.isFinite(_numeric) ? _numeric : 0;
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type AuditView = ViewType<_AuditView, Audit>;

export function AuditViewProxy(object: Audit, data: dataService): AuditView {
    return new _AuditView(object, data).Proxy as AuditView;
}
